import { createContext, useContext } from 'react';
import { runInAction, toJS, makeAutoObservable } from 'mobx';
import UWProductsModule from 'store/modules/uwProducts/uwProductsModule';
import { PlanAttributeCategory, ProductTemplate } from 'types/productServiceV2';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { getFullProductTemplateInfo, saveRequiredAnswers, saveCustomAnswers } from 'api/operations/productServiceV2';
import { generateAttrFormByPolicyAttrAnswers, INITIAL_FORM_OBJECT, requestFormById } from 'api/operations/formService';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { getDomainProductLine } from 'api/operations/domainServiceV2';

interface AttributeNavigationResult {
  attributeId: string;
  attributeCategory: PlanAttributeCategory | null;
}

// TODO split into 2 stores(custom and required)

// This is a part of uw-products section, but is inherently different and hence don't use their abstract class.
// It may need a separate abstract class in future if there are others similar pages.
export default class RFQConfigurePlanAttributesStore {
  rootStore: RootStore;
  uwProductsModule: UWProductsModule;

  private _isLoading = false;
  private _planAttributeIDs: string[] = [];
  private _currentAttributeId: string = '';
  private _planAttributesBaseFormId = '';
  private _planAttributesCustomFormId = '';

  // Configure attribute form
  private _form: FormObjectFromJSON = INITIAL_FORM_OBJECT;
  private _dynamicForm: null | formUtils.DynamicFormController = null;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get form(): FormObjectFromJSON {
    return this._form;
  }

  public get dynamicForm(): null | formUtils.DynamicFormController {
    return this._dynamicForm;
  }

  private get productTemplate(): ProductTemplate | null {
    return this.uwProductsModule.productTemplate;
  }

  private get planAttributeIDs(): string[] {
    return this._planAttributeIDs;
  }

  private get currentAttributeId(): string {
    return this._currentAttributeId;
  }

  public get currentAttributeName(): string {
    return this.uwProductsModule.getPlanAttributeNameById(this.currentAttributeId);
  }

  public get attributesProgressStatus(): string {
    return this.uwProductsModule.attributesProgressStatus;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  private setForm(form: FormObjectFromJSON): void {
    this._form = form;
  }

  private setDynamicForm(dynamicForm: null | formUtils.DynamicFormController): void {
    this._dynamicForm = dynamicForm;
  }

  private setCurrentAttributeId(currentAttributeId: string): void {
    this._currentAttributeId = currentAttributeId;
  }

  private setProductTemplate(template: ProductTemplate | null): void {
    this.uwProductsModule.setProductTemplate(template);
  }

  private setPlanAttributeIDs(ids: string[]): void {
    this._planAttributeIDs = ids;
  }

  private setPlanAttributesBaseFormId(id: string): void {
    this._planAttributesBaseFormId = id;
  }

  private setPlanAttributesCustomFormId(id: string): void {
    this._planAttributesCustomFormId = id;
  }

  // Api calls
  public async getInitialData(
    templateId: string,
    attributeId: string,
    attributeCategory: PlanAttributeCategory | null,
  ): Promise<void> {
    this.setIsLoading(true);
    // Common initial requests from module
    await this.uwProductsModule.getInitialData(templateId);

    await runInAction(async () => {
      // Set data
      this.setCurrentAttributeId(attributeId);
      this.setPlanAttributeIDs(this.uwProductsModule.planAttributeIDs);

      // Specific page request
      if (this.productTemplate) {
        await this.getAttributeFormId(this.productTemplate);

        if (attributeCategory === PlanAttributeCategory.REQUIRED) {
          await this.getRequiredPlanAttributeForm(attributeId);
        } else {
          await this.getCustomPlanAttributeForm(attributeId);
        }
      }
      this.setIsLoading(false);
    });
  }

  private async getAttributeFormId(productTemplate: ProductTemplate): Promise<void> {
    const effectiveDate = productTemplate.effectiveFrom;

    const res = await getDomainProductLine({
      effectiveDate,
      zipCode: '',
      state: '',
      county: '',
      agencyId: '',
      sponsorId: this.uwProductsModule.rootStore.userStore.sponsorId,
    });

    runInAction(() => {
      const currentProductLine = res.find((pLine) => pLine.productLineId === productTemplate.productLineId);
      this.setPlanAttributesBaseFormId(currentProductLine?.planAttributesBaseFormId || '');
      this.setPlanAttributesCustomFormId(currentProductLine?.planAttributesCustomFormId || '');
    });
  }

  public async getFormByAttributeId(attributeId: string, updateTemplate = false): Promise<void> {
    if (updateTemplate && this.productTemplate) {
      await this.getProductTemplate(toJS(this.productTemplate).id);
    }
    await runInAction(async (): Promise<void> => {
      const getFormRequest =
        this.uwProductsModule.getPlanAttributeCategoryById(attributeId) === PlanAttributeCategory.REQUIRED
          ? this.getRequiredPlanAttributeForm
          : this.getCustomPlanAttributeForm;

      if (this.productTemplate) {
        this.setIsLoading(true);
        await getFormRequest(attributeId);
        runInAction(() => {
          this.setCurrentAttributeId(attributeId);
          this.setIsLoading(false);
        });
      }
    });
  }

  private async getCustomPlanAttributeForm(attributeId: string): Promise<void> {
    this.setDynamicForm(null);
    this.setForm(INITIAL_FORM_OBJECT);

    const form = await requestFormById(this._planAttributesCustomFormId);
    runInAction(() => {
      this.setForm(form);
      // If attributeId is empty, it means that we are creating a new custom attribute
      this.createFormController(
        attributeId ? { ...this.productTemplate?.formAnswers?.customAttributes[attributeId] } : {},
      );
    });
  }

  private async getRequiredPlanAttributeForm(attributeId: string): Promise<void> {
    this.setDynamicForm(null);
    this.setForm(INITIAL_FORM_OBJECT);
    const convertedTemplate = toJS(this.productTemplate);

    const res = await generateAttrFormByPolicyAttrAnswers(
      this._planAttributesBaseFormId,
      attributeId,
      this.rootStore.userStore.sponsorId,
      convertedTemplate?.formAnswers?.policyAttributes || {},
    );
    runInAction(() => {
      if (res?.data?.generateAttrFormByPolicyAttrAnswers) {
        this.setForm(res.data.generateAttrFormByPolicyAttrAnswers);
        this.createFormController(convertedTemplate?.formAnswers.planAttributes[attributeId] || {});
      }
    });
  }

  private createFormController(answers: Record<string, string>): void {
    this.setDynamicForm(new formUtils.DynamicFormController(this.form, this.rootStore.userStore.userRoles));
    this.dynamicForm?.populateFields(answers);
  }

  public async saveRequiredAnswers(attributeId: string): Promise<void> {
    this.setIsLoading(true);
    if (!this.productTemplate) return;

    const updatedTemplate = await saveRequiredAnswers(
      this.productTemplate.id,
      attributeId,
      this.dynamicForm?.getCanonicalAnswers() as Record<string, string>,
    );
    runInAction(() => {
      this.uwProductsModule.setProductTemplate(updatedTemplate);
      this.setIsLoading(false);
    });
  }

  public async saveCustomAnswers(): Promise<void> {
    this.setIsLoading(true);
    if (!this.productTemplate) return;
    const updatedTemplate = await saveCustomAnswers(
      this.productTemplate.id,
      this.dynamicForm?.getCanonicalAnswers() as Record<string, string>,
    );
    runInAction(() => {
      this.uwProductsModule.setProductTemplate(updatedTemplate);
      this.setIsLoading(false);
    });
  }

  public async getProductTemplate(templateId: string): Promise<void> {
    const res = await getFullProductTemplateInfo(templateId);
    runInAction((): void => {
      this.setProductTemplate(res || null);
    });
  }

  // Helpers

  public get canProceedWithNextForm(): boolean {
    const currentIndex = this.planAttributeIDs.indexOf(this.currentAttributeId);
    return currentIndex < this.planAttributeIDs.length - 1;
  }

  public get canReturnToPreviousForm(): boolean {
    const currentIndex = this.planAttributeIDs.indexOf(this.currentAttributeId);
    return currentIndex > 0;
  }

  public getNewAttribute(type: 'NEXT' | 'PREV', attributeId: string): AttributeNavigationResult {
    const currentIndex = this.planAttributeIDs.indexOf(attributeId);
    if (currentIndex < 0) return { attributeId: '', attributeCategory: null };

    switch (type) {
      case 'NEXT':
        if (currentIndex < this.planAttributeIDs.length - 1) {
          const nextAttributeId = this.planAttributeIDs[currentIndex + 1];
          const nextAttributeCategory = this.uwProductsModule.getPlanAttributeCategoryById(nextAttributeId);
          return { attributeId: nextAttributeId, attributeCategory: nextAttributeCategory };
        }
        break;
      case 'PREV':
        if (currentIndex > 0) {
          const prevAttributeId = this.planAttributeIDs[currentIndex - 1];
          const prevAttributeCategory = this.uwProductsModule.getPlanAttributeCategoryById(prevAttributeId);
          return { attributeId: prevAttributeId, attributeCategory: prevAttributeCategory };
        }
        break;

      default:
        return { attributeId: '', attributeCategory: null };
    }
    return { attributeId: '', attributeCategory: null };
  }

  public resetDynamicForm(): void {
    this.dynamicForm?.resetForm();
  }

  public resetInitialData(): void {
    this.setProductTemplate(null);
    this.setDynamicForm(null);
    this.setForm(INITIAL_FORM_OBJECT);
    this.setPlanAttributeIDs([]);
    this.setCurrentAttributeId('');
    this.uwProductsModule.resetInitialData();
  }

  constructor() {
    this.rootStore = getRootStoreInstance();
    this.uwProductsModule = new UWProductsModule();

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQConfigurePlanAttributesStore());

export const useRFQConfigurePlanAttributesStore = (): RFQConfigurePlanAttributesStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
